// Parses raw Tectonic/BibTeX logs into a compact, structured diagnostic
// list so the compile-failed banner can highlight actionable issues
// (unresolved references, missing citations, undefined control sequences)
// instead of dumping 1 KiB of trailing log text on the author.
//
// Runs client-side — the worker still returns the raw log and we keep
// showing an excerpt as a fallback for cases the parser doesn't cover.

export type CompileDiagnosticSeverity = 'error' | 'warning';

export type CompileDiagnosticCode =
  | 'undefined_reference'
  | 'undefined_citation'
  | 'missing_bib_entry'
  | 'undefined_control_sequence'
  | 'missing_dollar'
  | 'runaway_argument'
  | 'file_not_found'
  | 'tikz_error'
  | 'figure_placeholder'
  | 'latex_error'
  | 'bibtex_error';

export interface CompileDiagnostic {
  code: CompileDiagnosticCode;
  severity: CompileDiagnosticSeverity;
  message: string;
  target?: string;
  line?: number;
}

interface Rule {
  code: CompileDiagnosticCode;
  severity: CompileDiagnosticSeverity;
  re: RegExp;
  build: (m: RegExpExecArray) => Omit<CompileDiagnostic, 'code' | 'severity'>;
}

const RULES: Rule[] = [
  {
    code: 'undefined_reference',
    severity: 'warning',
    re: /LaTeX Warning: Reference [`'"]?([^'"`\s]+)[`'"]? on input line (\d+) undefined/gi,
    build: (m) => ({
      message: `Reference \\ref{${m[1]}} is undefined`,
      target: m[1],
      line: Number(m[2]),
    }),
  },
  {
    code: 'undefined_citation',
    severity: 'warning',
    re: /LaTeX Warning: Citation [`'"]?([^'"`\s]+)[`'"]? on page \d+ undefined on input line (\d+)/gi,
    build: (m) => ({
      message: `Citation \\cite{${m[1]}} is undefined`,
      target: m[1],
      line: Number(m[2]),
    }),
  },
  {
    code: 'missing_bib_entry',
    severity: 'error',
    // BibTeX log: `I didn't find a database entry for "key"`
    re: /I didn't find a database entry for ["']([^"']+)["']/gi,
    build: (m) => ({
      message: `Bibliography has no entry for "${m[1]}"`,
      target: m[1],
    }),
  },
  {
    code: 'undefined_control_sequence',
    severity: 'error',
    re: /!\s*Undefined control sequence\.[\s\S]{0,200}?(\\[A-Za-z@]+)/g,
    build: (m) => ({
      message: `Undefined control sequence ${m[1]}`,
      target: m[1],
    }),
  },
  {
    code: 'missing_dollar',
    severity: 'error',
    re: /!\s*Missing \$ inserted\./g,
    build: () => ({ message: 'Missing $ — likely math mode not opened/closed' }),
  },
  {
    code: 'runaway_argument',
    severity: 'error',
    re: /!\s*Runaway argument\?/g,
    build: () => ({ message: 'Runaway argument — check for unbalanced braces' }),
  },
  {
    code: 'file_not_found',
    severity: 'error',
    re: /!\s*LaTeX Error: File [`'"]?([^'"`\s]+)[`'"]? not found/gi,
    build: (m) => ({
      message: `File not found: ${m[1]}`,
      target: m[1],
    }),
  },
  {
    code: 'bibtex_error',
    severity: 'error',
    re: /^(.*---line \d+ of file [^\n]+)$/gm,
    build: (m) => ({ message: `BibTeX: ${m[1].trim()}` }),
  },
  {
    code: 'latex_error',
    severity: 'error',
    re: /!\s*LaTeX Error:\s*([^\n]+)/g,
    build: (m) => ({ message: m[1].trim().replace(/\.$/, '') }),
  },
];

// ── Tectonic v2 condensed diagnostics ────────────────────────────────
//
// Tectonic does NOT emit classic `! LaTeX Error:` lines on stdout. It
// prints its own condensed form:
//
//   error: main.tex:54: Misplaced alignment tab character &
//   warning: main.tex:53: Overfull \hbox (9.24pt too wide) ...
//   error: halted on potentially-recoverable error as specified
//
// Kept byte-for-byte in sync with
// `supabase/functions/_shared/compile-diagnostics.ts`.

const TECTONIC_LOCATED = /^(error|warning):\s+(?:([^\s:]+):)?(\d+):\s*(.+)$/;
const TECTONIC_PLAIN = /^(error|warning):\s+(.+)$/;

function isTectonicNoise(message: string): boolean {
  return (
    /Fontconfig error/i.test(message) ||
    /^(Over|Under)full \\[hv]box/i.test(message)
  );
}

const HALTED = /halted on potentially-recoverable error/i;

function classifyTectonic(
  message: string,
): { code: CompileDiagnosticCode; target?: string } {
  let m: RegExpMatchArray | null;
  if (/Package (?:pgf|tikz|pgfkeys) Error/i.test(message) || /I do not know the key/i.test(message)) {
    m = message.match(/(?:kind|key) [`'"]([^\n]+?)[`'"]\s*(?:[.,]|$| to | and )/i);
    return { code: 'tikz_error', target: m?.[1]?.replace(/^.*\//, '') };
  }
  if (/Undefined control sequence/i.test(message)) {
    m = message.match(/(\\[A-Za-z@]+)/);
    return { code: 'undefined_control_sequence', target: m?.[1] };
  }
  if (/Missing \$ inserted/i.test(message)) return { code: 'missing_dollar' };
  if (/Runaway argument/i.test(message)) return { code: 'runaway_argument' };
  if ((m = message.match(/File [`'"]?([^'"`\s]+)[`'"]? not found/i))) {
    return { code: 'file_not_found', target: m[1] };
  }
  if (/undefined/i.test(message)) {
    if ((m = message.match(/Citation [`'"]?([^'"`\s,]+)/i))) {
      return { code: 'undefined_citation', target: m[1] };
    }
    if ((m = message.match(/Reference [`'"]?([^'"`\s,]+)/i))) {
      return { code: 'undefined_reference', target: m[1] };
    }
  }
  if (/didn't find a database entry for ["']([^"']+)["']/i.test(message)) {
    return { code: 'missing_bib_entry' };
  }
  return { code: 'latex_error' };
}

interface Indexed extends CompileDiagnostic {
  _idx: number;
}

function parseTectonic(log: string): { items: Indexed[]; halted: Indexed | null } {
  const items: Indexed[] = [];
  let halted: Indexed | null = null;
  const seen = new Set<string>();
  let offset = 0;
  for (const rawLine of log.split('\n')) {
    const idx = offset;
    offset += rawLine.length + 1;
    const line = rawLine.trim();
    if (!line) continue;
    const located = TECTONIC_LOCATED.exec(line);
    const plain = located ? null : TECTONIC_PLAIN.exec(line);
    if (!located && !plain) continue;
    const severity = (located ? located[1] : plain![1]) as CompileDiagnosticSeverity;
    const message = (located ? located[4] : plain![2]).trim();
    if (!message || isTectonicNoise(message)) continue;
    const lineNo = located ? Number(located[3]) : undefined;
    if (HALTED.test(message)) {
      halted ??= {
        code: 'latex_error',
        severity: 'error',
        message: 'Compile halted on a LaTeX error',
        _idx: idx,
      };
      continue;
    }
    const { code, target } = classifyTectonic(message);
    const key = `${code}::${target ?? message}::${lineNo ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({
      code,
      severity,
      message,
      ...(target ? { target } : {}),
      ...(lineNo !== undefined ? { line: lineNo } : {}),
      _idx: idx,
    });
  }
  return { items, halted };
}

/**
 * Parse a raw compile log (Tectonic stdout/stderr, optionally with a
 * `----- BibTeX log (main.blg) -----` suffix) into a de-duplicated list
 * of structured diagnostics, ordered errors-first then by first
 * occurrence in the log.
 */
export function parseCompileLog(log: string | null | undefined): CompileDiagnostic[] {
  if (!log) return [];
  const seen = new Set<string>();
  const out: Indexed[] = [];
  for (const rule of RULES) {
    const re = new RegExp(rule.re.source, rule.re.flags.includes('g') ? rule.re.flags : `${rule.re.flags}g`);
    let m: RegExpExecArray | null;
    while ((m = re.exec(log)) !== null) {
      const partial = rule.build(m);
      const key = `${rule.code}::${partial.target ?? partial.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        code: rule.code,
        severity: rule.severity,
        _idx: m.index,
        ...partial,
      });
    }
  }

  const { items, halted } = parseTectonic(log);
  for (const d of items) {
    const key = `${d.code}::${d.target ?? d.message}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  if (out.length === 0 && halted) out.push(halted);

  out.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'error' ? -1 : 1;
    return a._idx - b._idx;
  });
  return out.map(({ _idx: _drop, ...d }) => d);
}

